import React, { useState, useEffect } from "react";
import { Script, withPrefix } from "gatsby";
import { useT, Link } from "../../hooks/useT";
import classNames from "classnames";
import validator from "validator";
import isEmpty from "lodash/isEmpty";
import Layout from "../../components/Layout";
import { FastSpringProvider } from "../../components/FastSpring";
import Button from "../../components/Button";
import Icon from "../../components/Icon";
import Input from "../../components/forms/Input";
import imagePayment from "../../assets/payment.png";
import PricingFAQ from "../partials/PricingFAQ";
import Section from "../../components/Section";
import Panel from "../../components/Panel";
import {
  PICPICK_DISCOUNT,
  USE_2CHECKOUT_LIFETIME,
  getPrice,
  formatTablePrice,
} from "../../constants";

// ─── Sub-components ──────────────────────────────────────────────────────────

function PlanCard({
  selected,
  onClick,
  icon,
  title,
  badge,
  price,
  fullPrice,
  sublabel,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  icon: string;
  title: string;
  badge?: string;
  price: string;
  fullPrice: string | null;
  sublabel: string;
  children?: React.ReactNode;
}) {
  return (
    <div
      onClick={onClick}
      className={classNames(
        "p-4 rounded-xl border-2 cursor-pointer transition-all select-none",
        selected
          ? "border-orange-400 bg-orange-50"
          : "border-gray-200 bg-white hover:border-gray-300"
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={classNames(
            "mt-0.5 w-9 h-9 rounded-full flex items-center justify-center shrink-0",
            selected ? "bg-orange-100" : "bg-gray-100"
          )}
        >
          <Icon name={icon} size="small" color={selected ? "orange" : "grey"} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={classNames(
                "font-semibold text-sm",
                selected ? "text-orange-700" : "text-gray-700"
              )}
            >
              {title}
            </span>
            {badge && (
              <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                {badge}
              </span>
            )}
          </div>
          {children && (
            <div className="mt-2" onClick={(e) => e.stopPropagation()}>
              {children}
            </div>
          )}
        </div>
        <div className="text-right shrink-0">
          {fullPrice && (
            <div className="text-xs line-through text-gray-300">
              {fullPrice} USD
            </div>
          )}
          <div
            className={classNames(
              "text-lg font-bold",
              selected ? "text-orange-600" : "text-gray-800"
            )}
          >
            {price} USD
          </div>
          <div className="text-xs text-gray-400 mt-0.5">{sublabel}</div>
        </div>
      </div>
    </div>
  );
}

function Stepper({
  value,
  min,
  max,
  onChange,
}: {
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}) {
  const clamp = (n: number) => Math.min(max, Math.max(min, n));
  return (
    <div className="inline-flex items-center rounded-lg border border-gray-200 overflow-hidden text-sm bg-white">
      <button
        type="button"
        onClick={() => onChange(clamp(value - 1))}
        className="w-8 h-8 flex items-center justify-center border-0 bg-transparent hover:bg-gray-100 cursor-pointer text-gray-600 font-medium"
      >
        −
      </button>
      <input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(e) => {
          const n = parseInt(e.target.value, 10);
          if (!Number.isNaN(n)) onChange(clamp(n));
        }}
        className="w-12 text-center font-bold text-gray-800 text-sm border-0 bg-transparent outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
      <button
        type="button"
        onClick={() => onChange(clamp(value + 1))}
        className="w-8 h-8 flex items-center justify-center border-0 bg-transparent hover:bg-gray-100 cursor-pointer text-gray-600 font-medium"
      >
        +
      </button>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function BuyPage({ location }: { location: Location }) {
  const { t, locale } = useT();

  // Parse query params for backward compat with /buy/checkout?license=X&quantity=Y links
  const parseParams = () => {
    if (typeof window === "undefined")
      return { plan: "single", billing: "lifetime", qty: 5, coupon: null };
    const p = new URLSearchParams(location?.search || "");
    const license = p.get("license") || "subscription";
    const qty = Math.max(1, parseInt(p.get("quantity") ?? "", 10) || 1);
    const coupon = p.get("code")?.trim() || null;
    if (license === "enterprise")
      return { plan: "enterprise", billing: "lifetime", qty: 1, coupon };
    if (license === "subscription")
      return {
        plan: qty > 1 ? "team" : "single",
        billing: "subscription",
        qty,
        coupon,
      };
    return {
      plan: qty > 1 ? "team" : "single",
      billing: "lifetime",
      qty,
      coupon,
    };
  };

  const init = parseParams();

  const [plan, setPlan] = useState(init.plan); // "single" | "team" | "enterprise"
  const [billing, setBilling] = useState(init.billing); // "subscription" | "lifetime"
  const [quantity, setQuantity] = useState(Math.max(init.qty, 5)); // team quantity, min 2
  const coupon = init.coupon;

  const isEnterprise = plan === "enterprise";
  const effectiveLicense = isEnterprise
    ? billing === "subscription"
      ? "enterprise-subscription"
      : "enterprise"
    : billing;
  const effectiveQty = plan === "single" ? 1 : isEnterprise ? 1 : quantity;

  const totalPrice = getPrice(effectiveQty, effectiveLicense, PICPICK_DISCOUNT);
  const fullPrice = getPrice(effectiveQty, effectiveLicense, false);

  // Form state
  const [forCompany, setForCompany] = useState(init.plan !== "single");
  const [email, setEmail] = useState("");
  const [emailConfirm, setEmailConfirm] = useState("");
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [company, setCompany] = useState("");
  const [validationError, setValidationError] = useState(false);
  const [isValidForm, setIsValidForm] = useState(false);

  const err = (field: string) => {
    switch (field) {
      case "email":
        return !validator.isEmail(email);
      case "emailConfirm":
        return !validator.isEmail(emailConfirm) || email !== emailConfirm;
      case "firstname":
        return isEmpty(firstname);
      case "lastname":
        return isEmpty(lastname);
      case "company":
        return isEmpty(company);
      default:
        return false;
    }
  };

  useEffect(() => {
    const nameOk = forCompany
      ? !err("company")
      : !err("firstname") && !err("lastname");
    setIsValidForm(!err("email") && !err("emailConfirm") && nameOk);
  }, [forCompany, firstname, lastname, company, email, emailConfirm]);

  if (locale === "ko" && typeof window !== "undefined") {
    window.location.replace("/");
    return null;
  }

  // ── Payment handlers (preserved from checkout.js) ─────────────────────────

  const handleFastSpring = () => {
    const { fastspring } = window as any;
    let language = locale;
    if (locale === "zh-tw") language = "zh";
    let country = locale;
    if (locale === "zh") country = "CN";
    if (locale === "zh-tw") country = "TW";
    const pathMap: Record<string, string> = {
      subscription: "picpick-subscription-2026",
      lifetime: "picpick-lifetime",
      enterprise: "picpick-enterprise-lifetime",
      "enterprise-subscription": "picpick-enterprise-subscription",
    };
    const session = {
      reset: true,
      products: [{ path: pathMap[effectiveLicense], quantity: effectiveQty }],
      paymentContact: {
        email,
        firstName: firstname,
        lastName: lastname,
        ...(forCompany ? { company } : {}),
      },
      language,
      country,
      ...(coupon ? { coupon } : {}),
    };
    fastspring.builder.push(session);
    fastspring.builder.checkout();
  };

  const handle2Checkout = () => {
    const { TwoCoInlineCart } = window as any;
    const codeMap: Record<string, string> = {
      subscription: "F11FF202F9",
      lifetime: "L86IF022E5",
      enterprise: "CE62F62BBB",
      "enterprise-subscription": "CE62F62BBB", // TODO: replace with actual enterprise subscription product code
    };
    const code = codeMap[effectiveLicense];
    let language = locale;
    if (locale === "zh-tw") language = "zy";
    else if (locale === "pt") language = "pt-br";

    if (TwoCoInlineCart) {
      TwoCoInlineCart.setup.setMerchant("NGWIN");
      TwoCoInlineCart.register();
      TwoCoInlineCart.cart.setReset(true);
      TwoCoInlineCart.products.removeAll();
      TwoCoInlineCart.products.add({ code, quantity: effectiveQty });
      TwoCoInlineCart.cart.setLanguage(language);
      TwoCoInlineCart.cart.setCurrency("USD");
      TwoCoInlineCart.cart.setAutoAdvance(true);
      TwoCoInlineCart.billing.setEmail(email);
      if (forCompany) TwoCoInlineCart.billing.setCompanyName(company);
      else TwoCoInlineCart.billing.setName(`${firstname} ${lastname}`);
      if ((window as any).localStorage?.getItem("ORDER_TEST"))
        TwoCoInlineCart.cart.setTest(true);
      TwoCoInlineCart.cart.checkout();
      return;
    }

    const fallbacks: Record<string, string> = {
      enterprise:
        "https://secure.2checkout.com/checkout/buy?merchant=NGWIN&tpl=one-column&prod=CE62F62BBB&qty=1&empty-cart=1",
      lifetime: `https://secure.2checkout.com/checkout/buy?merchant=NGWIN&tpl=one-column&prod=L86IF022E5&qty=${effectiveQty}&empty-cart=1`,
      subscription: `https://secure.2checkout.com/checkout/buy?merchant=NGWIN&tpl=one-column&prod=F11FF202F9&qty=${effectiveQty}&empty-cart=1`,
    };
    window.open(fallbacks[effectiveLicense]);
  };

  const handleSubmit = () => {
    if (!isValidForm) {
      setValidationError(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const isLifetime =
      effectiveLicense === "lifetime" || effectiveLicense === "enterprise";
    if (isLifetime && USE_2CHECKOUT_LIFETIME) handle2Checkout();
    else handleFastSpring();
  };

  // ── Helpers ───────────────────────────────────────────────────────────────

  const orderLabel = () => {
    const billingLabel =
      billing === "lifetime"
        ? t("buy_order_lifetime_label")
        : t("buy_order_subscription_label");
    if (isEnterprise) {
      return `${t("buy_order_enterprise_name")} · ${billingLabel} · ${t(
        "buy_order_unlimited_users"
      )}`;
    }
    if (plan === "single")
      return `PicPick Pro ${billingLabel} · 1 ${t("buy_order_user_singular")}`;
    return `PicPick Pro ${billingLabel} · ${quantity} ${t(
      "buy_order_users_plural"
    )}`;
  };

  const planPrice = (lic: string, qty: number) => ({
    total: formatTablePrice(getPrice(qty, lic, PICPICK_DISCOUNT)),
    full: PICPICK_DISCOUNT ? formatTablePrice(getPrice(qty, lic, false)) : null,
  });

  const singleP = planPrice(billing, 1);
  const teamP = planPrice(billing, quantity);
  const entP = planPrice(
    billing === "subscription" ? "enterprise-subscription" : "enterprise",
    1
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <FastSpringProvider>
      <Layout>
        {USE_2CHECKOUT_LIFETIME && (
          <Script src={withPrefix("2checkout.js")} type="text/javascript" />
        )}

        <Section
          titleAs="h1"
          title={t("buy_page_title")}
          description={t("buy_page_desc")}
          bg="slate"
          padding="md"
          className="min-h-screen"
          containerClassName="max-w-6xl"
        >
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
            {/* ── LEFT: Plan configurator ─────────────────────────────── */}
            <div className="lg:col-span-3 space-y-4">
              {/* Billing type */}
              <Panel
                title={t("buy_license_type")}
                titleClassName="text-xs font-semibold uppercase tracking-widest text-gray-400"
                titleBorder={false}
              >
                <div className="grid grid-cols-2 gap-2">
                  {[
                    {
                      key: "subscription",
                      label: t("buy_billing_subscription_label"),
                      sub: t("buy_billing_subscription_sub"),
                    },
                    {
                      key: "lifetime",
                      label: t("buy_billing_lifetime_label"),
                      sub: t("buy_billing_lifetime_sub"),
                      offer: t("buy_billing_lifetime_offer"),
                    },
                  ].map(({ key, label, sub, offer }) => {
                    const active = billing === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setBilling(key)}
                        className={classNames(
                          "py-3 px-4 rounded-xl border-2 text-left transition-all",
                          active
                            ? "border-orange-400 bg-orange-50"
                            : "border-gray-200 hover:border-gray-300"
                        )}
                      >
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span
                            className={classNames(
                              "font-semibold text-sm",
                              active ? "text-orange-700" : "text-gray-700"
                            )}
                          >
                            {label}
                          </span>
                          {offer && (
                            <span className="text-xs font-medium text-white bg-orange-400 px-1.5 py-0.5 rounded-full">
                              {offer}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5">
                          {sub}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </Panel>

              {/* Plan cards */}
              <Panel
                contentClassName="space-y-4"
                title={t("buy_select_plan")}
                titleClassName="text-xs font-semibold uppercase tracking-widest text-gray-400"
                titleBorder={false}
              >
                <PlanCard
                  selected={plan === "single"}
                  onClick={() => setPlan("single")}
                  icon="user"
                  title={t("buy_plan_single")}
                  price={singleP.total}
                  fullPrice={singleP.full}
                  sublabel={
                    billing === "subscription"
                      ? t("buy_plan_per_year")
                      : t("buy_plan_one_time")
                  }
                />

                <PlanCard
                  selected={plan === "team"}
                  onClick={() => setPlan("team")}
                  icon="users"
                  title={t("buy_plan_team_label")}
                  price={teamP.total}
                  fullPrice={teamP.full}
                  sublabel={`${
                    billing === "subscription"
                      ? t("buy_plan_per_year")
                      : t("buy_plan_one_time")
                  } · ${quantity} ${t("buy_order_users_plural")}`}
                >
                  {plan === "team" && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">
                        {t("buy_plan_users_stepper_label")}
                      </span>
                      <Stepper
                        value={quantity}
                        min={2}
                        max={200}
                        onChange={setQuantity}
                      />
                    </div>
                  )}
                </PlanCard>

                <PlanCard
                  selected={plan === "enterprise"}
                  onClick={() => setPlan("enterprise")}
                  icon="building"
                  title={t("buy_plan_enterprise_label")}
                  price={entP.total}
                  fullPrice={entP.full}
                  sublabel={
                    billing === "subscription"
                      ? t("buy_plan_per_year")
                      : t("buy_plan_enterprise_sub")
                  }
                />
              </Panel>

              {/* Volume pricing table */}
              <Panel
                title={
                  <>
                    {t("buy_volume_title")}{" "}
                    <span className="normal-case font-normal">
                      {t("buy_volume_per_user_label")}
                    </span>
                  </>
                }
                titleClassName="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3"
                titleBorder={false}
              >
                <table className="w-full text-sm text-gray-600 border-collapse">
                  <thead>
                    <tr className="border-b border-gray-100 text-xs text-gray-400 uppercase tracking-wide">
                      <th className="text-left py-2 font-medium">
                        {t("buy_volume_users_col")}
                      </th>
                      <th className="text-right py-2 font-medium">
                        {billing === "subscription"
                          ? t("buy_volume_per_year_col")
                          : t("buy_volume_one_time_col")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { label: "1", count: 1 },
                      { label: "2 – 4", count: 2 },
                      { label: "5 – 9", count: 5 },
                      { label: "10 – 24", count: 10 },
                      { label: "25 – 99", count: 25 },
                      { label: "100+", count: 100 },
                    ].map(({ label, count }) => {
                      const unitFull = getPrice(count, billing, false) / count;
                      const unitDiscount =
                        getPrice(count, billing, true) / count;
                      return (
                        <tr
                          key={count}
                          className="border-b border-gray-50 last:border-0"
                        >
                          <td className="py-2">{label}</td>
                          <td className="py-2 text-right">
                            {PICPICK_DISCOUNT ? (
                              <>
                                <span className="line-through text-gray-300 mr-2">
                                  {formatTablePrice(unitFull)}
                                </span>
                                <span className="text-red-500 font-medium">
                                  {formatTablePrice(unitDiscount)}
                                </span>
                              </>
                            ) : (
                              <span>{formatTablePrice(unitFull)}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p className="text-xs text-gray-400 mt-3">
                  {t("buy_volume_contact")}{" "}
                  <Link
                    to="/support"
                    className="text-orange-500 hover:underline"
                  >
                    {t("buy_volume_contact_link")}
                  </Link>
                </p>
              </Panel>

              <ul className="text-xs text-gray-400 space-y-1 px-1 list-disc list-inside">
                <li>{t("buy_note_license")}</li>
                <li>{t("buy_note_updates")}</li>
              </ul>
            </div>

            {/* ── RIGHT: Checkout form ────────────────────────────────── */}
            <div className="lg:col-span-2 lg:sticky lg:top-6">
              <Panel
                title={t("buy_your_details")}
                titleClassName="font-bold text-lg text-gray-900"
                titleBorder={false}
                className="shadow-sm"
              >
                {/* Form */}
                <div className="space-y-4">
                  {/* Individual / Company toggle */}
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { val: false, label: t("buy_individual") },
                      { val: true, label: t("buy_company_toggle") },
                    ].map(({ val, label }) => (
                      <button
                        key={label}
                        type="button"
                        onClick={() => setForCompany(val)}
                        className={classNames(
                          "py-2 rounded-lg border text-sm font-medium transition-all",
                          forCompany === val
                            ? "border-sky-400 bg-sky-50 text-sky-700"
                            : "border-gray-200 text-gray-500 hover:border-gray-300"
                        )}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  {forCompany ? (
                    <Input
                      label={t("buy_company_name_label")}
                      name="company"
                      value={company}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setCompany(e.target.value)
                      }
                      invalid={validationError && err("company")}
                    />
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        label={t("buy_first_name_label")}
                        name="firstname"
                        value={firstname}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setFirstname(e.target.value)
                        }
                        invalid={validationError && err("firstname")}
                      />
                      <Input
                        label={t("buy_last_name_label")}
                        name="lastname"
                        value={lastname}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setLastname(e.target.value)
                        }
                        invalid={validationError && err("lastname")}
                      />
                    </div>
                  )}

                  <Input
                    label={t("buy_email_address_label")}
                    placeholder={t("buy_email_placeholder")}
                    name="email"
                    value={email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setEmail(e.target.value)
                    }
                    invalid={validationError && err("email")}
                  />
                  <Input
                    label={t("buy_confirm_email_label")}
                    placeholder={t("buy_email_placeholder")}
                    name="emailConfirm"
                    value={emailConfirm}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setEmailConfirm(e.target.value)
                    }
                    invalid={validationError && err("emailConfirm")}
                  />
                </div>

                {/* Order summary + CTA */}
                <div className="mt-5 border-t border-gray-100 bg-gray-50 p-6 space-y-4">
                  <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">
                    {t("buy_order_summary_label")}
                  </p>

                  <div className="flex justify-between items-start gap-4">
                    <span className="text-sm text-gray-600 leading-snug">
                      {orderLabel()}
                    </span>
                    <div className="text-right shrink-0">
                      {PICPICK_DISCOUNT && (
                        <div className="text-xs line-through text-gray-300">
                          {formatTablePrice(fullPrice)} USD
                        </div>
                      )}
                      <div className="font-bold text-gray-900">
                        {formatTablePrice(totalPrice)} USD
                      </div>
                    </div>
                  </div>

                  {coupon && (
                    <div className="flex items-start gap-2 rounded-lg border border-orange-100 bg-orange-50 px-3 py-2 text-sm text-orange-700">
                      <Icon
                        name="info circle"
                        color="orange"
                        size="small"
                        className="mt-1"
                      />
                      <span>{t("buy_coupon_notice", { coupon })}</span>
                    </div>
                  )}

                  <Button
                    onClick={handleSubmit}
                    color="green"
                    size="large"
                    className="w-full"
                  >
                    <Icon name="lock" />
                    {t("buy_proceed_payment")}
                  </Button>

                  <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
                    <Icon name="shield" color="green" size="small" />
                    {t("buy_vat_notice")}
                  </div>
                </div>

                {/* Payment logos */}
                <div className="border-t border-gray-100 p-4 flex justify-center">
                  <img
                    src={imagePayment}
                    alt={t("buy_payment_img_alt")}
                    className="max-h-8 opacity-60"
                  />
                </div>
              </Panel>

              <p className="text-center text-sm text-gray-400 mt-4">
                {t("buy_have_license")}{" "}
                <a
                  href="https://www.picpick.org/license"
                  className="text-orange-500 hover:underline"
                >
                  {t("buy_manage_license")}
                </a>
              </p>
            </div>
          </div>
        </Section>

        <PricingFAQ />
      </Layout>
    </FastSpringProvider>
  );
}
